import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Button, Modal, Space, Table, message, Pagination } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import { API_URL, TOKEN } from "../../../variables/constants";
import AddAccessoires from "./AddAccessoires";
import ActionDropdown from "../../production/components/ActionDropdown";

const Accessoires = () => {
  const [accessoires, setAccessoires] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedAccessoire, setSelectedAccessoire] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false); // New state for table loading

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Allow changing page size, default 10

  const handleShow = (edit = false, accessoire = null) => {
    setEditMode(edit);
    setSelectedAccessoire(accessoire);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedAccessoire(null);
  };

  const fetchAccessoires = async () => {
    setLoadingTable(true); // Start loading
    try {
      const res = await axios.get(`${API_URL}/api/parametre/type/ACCESSOIRES`, {
        headers: { Authorization: `Bearer ${TOKEN}` },
      });
      // Sort by parametreId in descending order to show latest first
      // If your API provides a 'createdAt' timestamp, use that for more accurate "latest" sorting.
      const sortedAccessoires = res.data.sort(
        (a, b) => b.parametreId - a.parametreId
      );
      setAccessoires(sortedAccessoires);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la récupération des accessoires.");
    } finally {
      setLoadingTable(false); // End loading
    }
  };

  useEffect(() => {
    fetchAccessoires();
  }, []); // Fetch on initial component mount only

  // --- Handlers for Add and Update to keep new/modified at top ---
  const handleAddSuccess = (newAccessoire) => {
    setAccessoires((prev) => [newAccessoire, ...prev]); // Add new at the top
    setCurrentPage(1); // Go to the first page to see the new item
  };

  const handleUpdateSuccess = (updatedAccessoire) => {
    setAccessoires((prev) => {
      const withoutOld = prev.filter(
        (acc) => acc.parametreId !== updatedAccessoire.parametreId
      );
      return [updatedAccessoire, ...withoutOld]; // Place updated at the top
    });
    // The current page usually doesn't need to change for an update,
    // as the item will simply move to the top of the list, which might be on the current page or page 1.
  };
  // --- End Handlers ---

  const handleDelete = (parametreId) => {
    Modal.confirm({
      title: "Êtes-vous sûr de vouloir supprimer cet accessoire ?",
      icon: <ExclamationCircleOutlined />,
      content: "Cette action est irréversible",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        // Made onOk async
        try {
          await axios.delete(`${API_URL}/api/parametre/${parametreId}`, {
            headers: { Authorization: `Bearer ${TOKEN}` },
          });
          setAccessoires((prev) =>
            prev.filter((acc) => acc.parametreId !== parametreId)
          );
          message.success("Accessoire supprimé avec succès.");

          // After deletion, check if the current page is now empty
          // or if the last item on the last page was deleted.
          const totalItemsAfterDeletion = accessoires.length - 1;
          const totalPagesAfterDeletion = Math.ceil(
            totalItemsAfterDeletion / pageSize
          );
          if (
            currentPage > totalPagesAfterDeletion &&
            totalPagesAfterDeletion > 0
          ) {
            setCurrentPage(totalPagesAfterDeletion);
          } else if (totalPagesAfterDeletion === 0) {
            setCurrentPage(1); // Reset to page 1 if all items are deleted
          }
        } catch (err) {
          console.error(err);
          message.error("Erreur lors de la suppression de l'accessoire.");
        }
      },
    });
  };

  const columns = [
    {
      title: "Montant",
      dataIndex: "libelleParametre",
      key: "montant",
      render: (val) => new Intl.NumberFormat("fr-FR").format(Number(val)), // Ensure val is number for formatting
    },
    {
      title: "Catégorie",
      dataIndex: ["categorie", "nom"],
      key: "categorie",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <ActionDropdown
          actions={[
            {
              key: "edit",
              label: "Modifier",
              icon: <i className="bx bx-edit-alt me-1" />,
              onClick: () => handleShow(true, record),
            },
            {
              key: "delete",
              label: "Supprimer",
              icon: <i className="bx bx-trash me-1" />,
              danger: true,
              onClick: () => handleDelete(record.parametreId),
            },
          ]}
        />
      ),
    },
  ];

  // Pagination logic to slice data for the current page
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = accessoires.slice(startIndex, endIndex);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="TARIFICATION" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
                onClick={() => handleShow()}
              >
                Ajouter de nouveaux frais accessoires
              </Button>
              <div className="card">
                <h5 className="card-header">
                  Paramétrage de frais accessoires
                </h5>
                <div className="table-responsive text-nowrap">
                  {loadingTable ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: "200px" }}
                    >
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                    </div>
                  ) : paginatedData.length > 0 ? (
                    <Table
                      columns={columns}
                      dataSource={paginatedData}
                      rowKey="parametreId"
                      pagination={false} // Disable Ant Design's internal pagination
                      // Ant Design Table also has a `loading` prop if you want to use its built-in spinner:
                      // loading={loadingTable}
                    />
                  ) : (
                    <p className="text-center p-3">Aucun accessoire trouvé.</p>
                  )}
                  {accessoires.length > pageSize && ( // Only show pagination if there's more than one page
                    <div className="d-flex justify-content-end me-3 mb-3 mt-3">
                      <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={accessoires.length}
                        onChange={(page, size) => {
                          // Allow changing page size
                          setCurrentPage(page);
                          setPageSize(size);
                        }}
                        showSizeChanger // Enable size changer
                        pageSizeOptions={["5", "10", "20", "50"]} // Options for page size
                        showTotal={(total, range) =>
                          `${range[0]}-${range[1]} sur ${total} éléments`
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <AddAccessoires
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedAccesoire={selectedAccessoire}
        onAddSuccess={handleAddSuccess}
        onUpdateSuccess={handleUpdateSuccess} // Pass the new update success handler
        existingAccessoires={accessoires} // Pass the full list for client-side duplicate check
      />
    </div>
  );
};

export default Accessoires;
